import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { Activity, Users, MessageCircle, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageToggle } from '../components/LanguageToggle';

// Shared public demo account. Safe to keep client-side: after the rules
// rewrite this is a plain guest, no longer an admin account.
const DEMO_EMAIL = 'guest@ledger.com';
const DEMO_PASSWORD = 'guest123';

export const Demo = () => {
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const isHe = language === 'he';

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleEnterDemo = async () => {
        setError('');
        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, DEMO_EMAIL, DEMO_PASSWORD);
            navigate('/dashboard');
        } catch (err) {
            console.error('[Demo] Sign-in error:', err);
            setError(isHe ? 'לא הצלחנו להיכנס לדמו כרגע. נסו שוב.' : 'Could not enter the demo right now. Please try again.');
            setLoading(false);
        }
    };

    const features = [
        { icon: Activity, label: isHe ? 'ניהול משחק חי' : 'Live game management' },
        { icon: Users, label: isHe ? 'רישום שחקנים' : 'Player registration' },
        { icon: MessageCircle, label: isHe ? 'סוכן WhatsApp' : 'WhatsApp agent' },
    ];

    const EnterArrow = isHe ? ArrowLeft : ArrowRight;

    return (
        <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="absolute top-4 right-4">
                <LanguageToggle />
            </div>

            <div className="card-premium w-full max-w-md p-7 sm:p-10 text-center">
                <div className="mb-7 animate-floaty">
                    <span className="chip-upcoming uppercase tracking-widest text-[10px]">
                        {isHe ? 'חוויית דמו' : 'Demo experience'}
                    </span>
                    <h1 className="font-display text-5xl font-black text-gold-gradient mt-4 tracking-tighter">
                        {t('appName')}
                    </h1>
                </div>

                <p className="text-slate-400 leading-relaxed mb-8">
                    {isHe
                        ? 'ניהול משחקי פוקר חיים, רישום שחקנים וסוכן WhatsApp — הכול במקום אחד.'
                        : 'Live poker game management, player registration and a WhatsApp agent — all in one place.'}
                </p>

                <div className="grid grid-cols-3 gap-3 mb-8">
                    {features.map(({ icon: Icon, label }) => (
                        <div key={label} className="glass hover-lift p-3 flex flex-col items-center gap-2">
                            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-ink-800/70 border border-gold-500/20">
                                <Icon className="w-4 h-4 text-gold-400" />
                            </span>
                            <span className="text-[11px] leading-tight text-slate-300 font-semibold">{label}</span>
                        </div>
                    ))}
                </div>

                {error && (
                    <div className="glass border-felt-500/20 px-4 py-3 mb-5 text-sm text-slate-200">
                        {error}
                    </div>
                )}

                <button
                    onClick={handleEnterDemo}
                    disabled={loading}
                    className="btn-gold w-full py-4 text-base font-black tracking-tight"
                >
                    {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                        <>
                            <span>{isHe ? 'היכנסו לדמו' : 'Enter demo'}</span>
                            <EnterArrow className="w-5 h-5" />
                        </>
                    )}
                </button>

                <p className="mt-6 text-[10px] text-slate-500 uppercase tracking-widest font-bold">
                    {isHe ? 'חשבון אורח משותף · ללא הרשמה' : 'Shared guest account · No sign-up'}
                </p>
            </div>
        </div>
    );
};
